import axios, { AxiosInstance } from 'axios'

import Academic from './Academic'
import Common from './Common'
import authConfig from 'src/configs/auth'
import UserManagement from './UserManagement'
import Aggregator from './Aggregator'
import Apply from './Apply'
import Finance from './Finance'
import { StatusCode } from 'src/context/common'
import mem from 'mem'
import Enrolment from './Enrolment'
import { baseApiURL, apiEndPoints } from './config'
import Operations from './Operations'
import Document from './Document'
import Student from './Student'
import GlobalCalendarDashboard from './global-calendar'

const cacheBypassFlag =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('cache') === '1'

const appAPIServer: AxiosInstance = axios.create()

export const AcademicService = new Academic(appAPIServer)
export const CommonService = new Common(appAPIServer)
export const UserManagementService = new UserManagement(appAPIServer)
export const AggregatorService = new Aggregator(appAPIServer)
export const ApplyService = new Apply(appAPIServer)
export const EnrolmentService = new Enrolment(appAPIServer)
export const FinanceService = new Finance(appAPIServer)
export const DashboardService = new Operations(appAPIServer)
export const DocumentService = new Document(appAPIServer)
export const StudentService = new Student(appAPIServer)
export const GlobalCalendarService = new GlobalCalendarDashboard(appAPIServer)

const refreshTokenUrl = `${baseApiURL + apiEndPoints.refreshToken}`

appAPIServer.interceptors.request.use(
  config => {
    if (config.headers && config?.url === refreshTokenUrl) {
      config.headers['Authorization'] = `Bearer ${window.localStorage.getItem(authConfig.refreshToken)}`
    } else if (config.headers) {
      config.headers['Authorization'] = `Bearer ${window.localStorage.getItem(authConfig.storageTokenKeyName)}`
      const azureRefreshToken = window.localStorage.getItem(authConfig.azureRefreshToken)
      if (azureRefreshToken) {
        config.headers['x-refresh-token'] = azureRefreshToken
      }
      const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone
      if (timezone) {
        config.headers['x-timezone'] = timezone
      }
    }
    if (cacheBypassFlag && config.headers) {
      config.headers['x-cache-bypass'] = 'true'
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

const refreshTokenFunction = async () => {
  const response = await UserManagementService.getRefreshToken()
  // eslint-disable-next-line no-unsafe-optional-chaining
  const { data } = response?.data
  if (data?.access_token && data?.refresh_token) {
    await window.localStorage.setItem(authConfig.storageTokenKeyName, data.access_token)
    await window.localStorage.setItem(authConfig.refreshToken, data.refresh_token)
    await window.localStorage.setItem(authConfig.azureRefreshToken, data.azureRefreshToken)
  }

  return data
}

const maxAge = 10000
const memoizedRefreshToken = mem(refreshTokenFunction, {
  maxAge
})

const requestInterceptor = (config: any) => {
  if (config.headers) {
    config.headers['Authorization'] = `Bearer ${window.localStorage.getItem(authConfig.storageTokenKeyName)}`
  }

  return config
}

const responseInterceptor = (response: any) => {
  if (response) {
    return response
  }
}
const errorInterceptor = async (err: any) => {
  const error = err.response
  const config = error?.config

  if (error.status === StatusCode.Unauthorize && !config?.sent && !config?.__isRetryRequest) {
    config.sent = true
    const response = await memoizedRefreshToken()
    if (response?.status === 200 && response?.access_token && response?.refresh_token) {
      config.headers['Authorization'] = `Bearer ${response?.access_token}`
    }

    return appAPIServer(config)
  }

  if (error.status === StatusCode.Unauthorize) {
    redirectToLoginPage()

    return
  }

  return Promise.reject(error)
}

const addInterceptorToAxiosInstances = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(requestInterceptor)
  axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor)
}

const redirectToLoginPage = async () => {
  let pathName = window.location.pathname
  pathName = pathName.replace(/^\/[\w\d]+\//, '')
  await window.localStorage.clear()
  window.location.href = `${process.env.NEXT_PUBLIC_ACADEMIC_BASE_URL}/login?returnUrl=/${pathName}`
}

addInterceptorToAxiosInstances(appAPIServer)
